import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCredit } from "../context/CreditContext";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "./ui/button";
import { LogOut, Wallet2, Plus, User, Sun, Moon, ShoppingCart } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "./ui/dropdown-menu";
import AuthModal from "./AuthModal";
import WalletModal from "./WalletModal";
import InventoryModal from "./InventoryModal";
import ProfileModal from "./ProfileModal";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("signin");
  const [profileOpen, setProfileOpen] = useState(false);

  const openAuth = (mode) => { setAuthMode(mode); setAuthOpen(true); };

  // Logged-out users clicking the deposit banner get prompted to sign in.
  React.useEffect(() => {
    const h = () => { if (!user) openAuth("signin"); };
    const a = () => openAuth("signin");
    window.addEventListener("uc:open-deposit", h);
    window.addEventListener("uc:open-auth", a);
    return () => { window.removeEventListener("uc:open-deposit", h); window.removeEventListener("uc:open-auth", a); };
  }, [user]);

  return (
    <header className="sticky top-0 z-40 border-b border-[#2A2440] dark:border-[#2A2440] bg-white/90 dark:bg-[#141021]/90 backdrop-blur-md">
      <div className="mx-auto max-w-[1500px] px-4 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="group flex items-center gap-2" data-testid="site-logo">
          <img
            src="/uc-box.png"
            alt="unboxcases logo"
            className="h-10 w-auto object-contain select-none transition-transform duration-200 group-hover:scale-[1.04]"
            draggable={false}
          />
          <div className="hidden sm:inline-flex flex-col leading-none">
            <span className="font-display text-[21px] sm:text-[23px] font-extrabold tracking-[-0.02em] lowercase text-white dark:text-white">
              unbox<span className="text-transparent bg-clip-text bg-gradient-to-r from-[#9945FF] to-[#14F195]">cases</span>
            </span>
            <div className="flex w-full justify-between text-[8px] mt-1 font-semibold uppercase text-[#9AA1B2]">
              {"MYSTERY UNBOXING".split("").map((ch, i) => (
                <span key={i}>{ch === " " ? "\u00A0" : ch}</span>
              ))}
            </div>
          </div>
        </Link>

        <div className="flex items-center gap-2 sm:gap-3">
          {user && <CreditWidget />}
          <button onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme"
            className="h-9 w-9 flex items-center justify-center rounded-lg border border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#1E1832] text-white dark:text-[#120F22] hover:bg-[#19142A] dark:hover:bg-[#2A2440] transition-colors">
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="user-menu-trigger"
                  className="rounded-lg border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#1E1832] hover:bg-[#19142A] dark:hover:bg-[#2A2440] text-white dark:text-[#120F22] gap-2 font-semibold">
                  <span className="h-6 w-6 rounded-full bg-[#7C4DFF] dark:bg-[#9945FF] text-white flex items-center justify-center"><User className="h-3.5 w-3.5" /></span>
                  <span className="hidden xs:inline max-w-[120px] truncate">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#19142A] dark:bg-[#1E1832] border-[#2A2440] dark:border-[#2A2440] rounded-lg w-52">
                <DropdownMenuLabel className="text-[#8A90A0] text-xs font-normal truncate">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#2A2440] dark:bg-[#2A2440]" />
                <DropdownMenuItem onClick={() => setProfileOpen(true)} data-testid="profile-menu-item"
                  className="gap-2 rounded-lg cursor-pointer text-white dark:text-[#120F22] focus:bg-[#19142A] dark:focus:bg-[#2A2440]">
                  <User className="h-4 w-4" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-[#DB2777] focus:bg-[#FDE8F1] dark:focus:bg-[#3a2230] focus:text-[#DB2777] gap-2 rounded-lg cursor-pointer">
                  <LogOut className="h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={() => openAuth("signin")} data-testid="signin-btn"
                className="px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold border border-[#2A2440] dark:border-[#2A2440] bg-[#19142A] dark:bg-[#1E1832] text-white dark:text-[#120F22] hover:bg-[#19142A] dark:hover:bg-[#2A2440] transition-colors">
                Sign in
              </button>
              <button onClick={() => openAuth("signup")} data-testid="signup-btn"
                className="px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold bg-[#7C4DFF] dark:bg-[#9945FF] text-white hover:bg-[#6A3AE6] dark:hover:bg-[#8236e6] transition-colors">
                Sign up
              </button>
            </div>
          )}
        </div>
      </div>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} />
      <ProfileModal open={profileOpen} onOpenChange={setProfileOpen} />
    </header>
  );
}

function CreditWidget() {
  const { credit } = useCredit();
  const [walletOpen, setWalletOpen] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const [depositPromo, setDepositPromo] = useState(null);

  React.useEffect(() => {
    const h = (e) => { setDepositPromo(e?.detail?.promo || null); setWalletOpen(true); };
    window.addEventListener("uc:open-deposit", h);
    return () => window.removeEventListener("uc:open-deposit", h);
  }, []);

  return (
    <>
      <div className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5">
        <Wallet2 className="h-4 w-4 text-[#0891B2]" />
        <span data-testid="credit-balance" className="text-sm font-semibold text-white dark:text-white font-grotesk">${credit.toFixed(2)}</span>
      </div>
      <button data-testid="inventory-open-btn" onClick={() => setInvOpen(true)} aria-label="View inventory"
        className="h-9 w-9 flex items-center justify-center rounded-lg border border-black/10 dark:border-white/10 bg-black/5 dark:bg-white/5 text-white dark:text-white hover:bg-black/10 dark:hover:bg-white/10 transition-colors">
        <ShoppingCart className="h-4 w-4" />
      </button>
      <button data-testid="wallet-open-btn" onClick={() => setWalletOpen(true)} aria-label="Open wallet"
        className="h-9 w-9 flex items-center justify-center rounded-lg bg-[#7C4DFF] dark:bg-[#9945FF] text-white hover:bg-[#6A3AE6] dark:hover:bg-[#8236e6] transition-colors">
        <Plus className="h-4 w-4" />
      </button>
      <WalletModal open={walletOpen} onOpenChange={(o) => { setWalletOpen(o); if (!o) setDepositPromo(null); }} initialTab="deposit" initialPromo={depositPromo} />
      <InventoryModal open={invOpen} onOpenChange={setInvOpen} />
    </>
  );
}
